<script setup>
import { computed, inject, ref } from "vue";

import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from "@headlessui/vue";

const emit = defineEmits(["close"]);

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  maxWidth: {
    type: String,
    default: "lg"
  },
  closeable: {
    type: Boolean,
    default: true
  },
  dim: {
    type: Boolean,
    default: true
  },
  dialogClass: {
    type: String,
    default: ""
  }
});

const dialogPanel = ref(null);

const close = () => {
  if (props.closeable) {
    emit("close");
  }
};

const maxWidthClass = computed(() => {
  return {
    sm: "sm:max-w-sm",
    md: "sm:max-w-md",
    lg: "sm:max-w-lg",
    xl: "sm:max-w-xl",
    "2xl": "sm:max-w-2xl",
    "screen-sm": "sm:max-w-screen-sm",
    "screen-md": "sm:max-w-screen-md",
    "screen-lg": "sm:max-w-screen-lg",
    "screen-xl": "sm:max-w-screen-xl",
    "screen-2xl": "sm:max-w-screen-2xl",
    full: "sm:max-w-full"
  }[props.maxWidth];
});

const setIsOpen = (isOpen) => {
  if (!isOpen) {
    close();
  }
};

const twMerge = inject("twMerge");
const dialogComputedClass = computed(() => twMerge("w-full min-w-72 rounded-xl bg-white p-4 shadow-soft", maxWidthClass.value, props.dialogClass));
</script>

<template>
  <TransitionRoot :show="show" as="template">
    <Dialog :open="show" class="dialog relative z-100" role="dialog" aria-modal="true" @close="setIsOpen">
      <TransitionChild
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <!-- The backdrop, rendered as a fixed sibling to the panel container -->
        <div class="fixed inset-0 bg-black/30 dark:bg-black/70" aria-hidden="true"></div>
      </TransitionChild>

      <!-- Full-screen scrollable container -->
      <div class="dialog-root isp-inset-0 fixed w-screen overflow-y-auto">
        <!-- Container to center the panel -->
        <TransitionChild
          class="flex min-h-full items-center justify-center p-4"
          enter="duration-300 ease-out"
          enter-from="opacity-0 scale-95"
          enter-to="opacity-100 scale-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100 scale-100"
          leave-to="opacity-0 scale-95"
        >
          <!-- The actual dialog panel -->
          <DialogPanel ref="dialogPanel" :class="dialogComputedClass">
            <slot />
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
